import React, { useState } from "react";
import { submitContact, submitCallback, errMessage } from "@/lib/api";
import { SITE, waLink } from "@/data/site";
import { MapPin, Phone, MessageCircle, Mail, Loader2, Check } from "lucide-react";

export default function Contact() {
  const [tab, setTab] = useState("contact");
  return (
    <main data-testid="contact-page" className="pt-24 md:pt-32 pb-24">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12">
        <div>
          <p className="chapter-index text-emerald-700">Contact · We're on your line</p>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3">Let's talk <span className="italic text-emerald-800">numbers.</span></h1>
          <ul className="mt-10 space-y-4 text-slate-700">
            <li className="flex items-start gap-3"><MapPin className="text-emerald-700 mt-1" size={18}/><div><p className="font-medium">{SITE.city}</p><p className="text-sm text-slate-500">Branches: {SITE.branches.join(", ")}</p></div></li>
            <li className="flex items-center gap-3"><Phone className="text-emerald-700" size={18}/><a href={`tel:${SITE.phoneRaw}`}>{SITE.phone}</a></li>
            <li className="flex items-center gap-3"><Phone className="text-emerald-700" size={18}/><a href={`tel:${SITE.supportRaw}`}>{SITE.support} (support)</a></li>
            <li className="flex items-center gap-3"><MessageCircle className="text-emerald-700" size={18}/><a href={waLink()} target="_blank" rel="noreferrer">WhatsApp us</a></li>
            <li className="flex items-center gap-3"><Mail className="text-emerald-700" size={18}/><a href="mailto:admin@rightwayfinance.co">admin@rightwayfinance.co</a></li>
          </ul>
          <div className="mt-10 rounded-3xl overflow-hidden aspect-[4/3] clip-frame">
            <img src="https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3ODQ0NDU5NDN8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover"/>
          </div>
        </div>
        <div>
          <div className="inline-flex rounded-full bg-white border border-slate-200 p-1 text-sm">
            <button data-testid="tab-contact" onClick={()=>setTab('contact')} className={`px-4 py-2 rounded-full ${tab==='contact'?'bg-slate-900 text-white':'text-slate-700'}`}>Message us</button>
            <button data-testid="tab-callback" onClick={()=>setTab('callback')} className={`px-4 py-2 rounded-full ${tab==='callback'?'bg-slate-900 text-white':'text-slate-700'}`}>Request callback</button>
          </div>
          {tab === 'contact' ? <ContactForm/> : <CallbackForm/>}
        </div>
      </section>
    </main>
  );
}

function ContactForm() {
  const [f, setF] = useState({ name:"", mobile:"", email:"", subject:"", message:"" });
  const [b, setB] = useState(false); const [ok, setOk] = useState(false); const [e, setE] = useState("");
  const on = (k) => (ev) => setF({...f,[k]:ev.target.value});
  async function submit(ev) {
    ev.preventDefault(); setE("");
    if (!f.name || !/^\d{10}$/.test(f.mobile) || !f.message) { setE("Fill name, valid mobile and message."); return; }
    setB(true); try {
      const payload = { ...f, email: f.email?.trim() ? f.email.trim() : undefined };
      await submitContact(payload); setOk(true);
    } catch(err){setE(errMessage(err));} finally{setB(false);}
  }
  if (ok) return <Success msg="We'll reach out within 24 hours."/>;
  return (
    <form onSubmit={submit} data-testid="contact-form" className="mt-6 rounded-3xl bg-white border border-black/[0.06] p-6 md:p-8 grid gap-3">
      <input required data-testid="contact-name" value={f.name} onChange={on('name')} placeholder="Full name" className="input"/>
      <input required data-testid="contact-mobile" value={f.mobile} onChange={on('mobile')} inputMode="numeric" pattern="\d{10}" placeholder="10-digit mobile" className="input"/>
      <input data-testid="contact-email" value={f.email} onChange={on('email')} type="email" placeholder="Email (optional)" className="input"/>
      <input data-testid="contact-subject" value={f.subject} onChange={on('subject')} placeholder="Subject" className="input"/>
      <textarea required data-testid="contact-message" value={f.message} onChange={on('message')} placeholder="Your message" rows={4} className="input"/>
      {e && <p className="text-sm text-red-600">{e}</p>}
      <button data-testid="contact-submit" disabled={b} className="btn-line rounded-full bg-emerald-700 text-white py-3 text-sm inline-flex items-center justify-center gap-2">{b?<Loader2 className="animate-spin" size={16}/>:'Send message'}</button>
      <style>{`.input{width:100%;border:1px solid hsl(214 32% 91%);background:#fff;border-radius:1rem;padding:.75rem 1rem;font-size:.9rem;outline:none}.input:focus{border-color:hsl(158 64% 40%)}`}</style>
    </form>
  );
}

function CallbackForm() {
  const [f, setF] = useState({ name:"", mobile:"", preferred_time:"", product_interest:"" });
  const [b, setB] = useState(false); const [ok, setOk] = useState(false); const [e, setE] = useState("");
  const on = (k) => (ev) => setF({...f,[k]:ev.target.value});
  async function submit(ev) {
    ev.preventDefault(); setE("");
    if (!f.name || !/^\d{10}$/.test(f.mobile)) { setE("Enter name and valid mobile."); return; }
    setB(true); try { await submitCallback(f); setOk(true);} catch(err){setE(errMessage(err));} finally{setB(false);}
  }
  if (ok) return <Success msg="We'll call you shortly."/>;
  return (
    <form onSubmit={submit} data-testid="callback-form" className="mt-6 rounded-3xl bg-white border border-black/[0.06] p-6 md:p-8 grid gap-3">
      <input required data-testid="cb-name" value={f.name} onChange={on('name')} placeholder="Full name" className="input"/>
      <input required data-testid="cb-mobile" value={f.mobile} onChange={on('mobile')} inputMode="numeric" pattern="\d{10}" placeholder="10-digit mobile" className="input"/>
      <input data-testid="cb-time" value={f.preferred_time} onChange={on('preferred_time')} placeholder="Preferred time (e.g. tomorrow 4-6pm)" className="input"/>
      <input data-testid="cb-interest" value={f.product_interest} onChange={on('product_interest')} placeholder="Product interest (optional)" className="input"/>
      {e && <p className="text-sm text-red-600">{e}</p>}
      <button data-testid="cb-submit" disabled={b} className="btn-line rounded-full bg-emerald-700 text-white py-3 text-sm inline-flex items-center justify-center gap-2">{b?<Loader2 className="animate-spin" size={16}/>:'Request callback'}</button>
      <style>{`.input{width:100%;border:1px solid hsl(214 32% 91%);background:#fff;border-radius:1rem;padding:.75rem 1rem;font-size:.9rem;outline:none}.input:focus{border-color:hsl(158 64% 40%)}`}</style>
    </form>
  );
}

function Success({ msg }) {
  return (
    <div data-testid="form-success" className="mt-6 rounded-3xl bg-emerald-50 border border-emerald-200 p-8">
      <div className="w-10 h-10 rounded-full bg-emerald-600 grid place-items-center text-white"><Check size={20}/></div>
      <p className="mt-4 font-display text-2xl text-slate-900">Received.</p>
      <p className="mt-2 text-slate-600">{msg}</p>
    </div>
  );
}
